import { useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";
import React from "react";
import { Grid, Typography } from "@mui/material";
import DetailRow from "../../pages/common-features/DetailRow";
import { camelToTitle } from "../Form/StepperDyanmicForm/helper";
import SummarySectionHeader from "./SummarySectionHeader";

interface AddressDetailsTabProps {
  addressDetails: any;
  isMobile: boolean;
  pageType?: string;
}

const AddressDetailsTab: React.FC<AddressDetailsTabProps> = ({
  addressDetails,
  isMobile,
  pageType = "DEFAULT",
}) => {
  const { fetchUserApplicationData } = useSelector(
    (state: RootState) => state.stepper
  );

  // Helper to map address fields from the vertical step to the required format
  function mapAddressFields(fields: any[], prefix: string) {
    if (!fields) return [];
    return fields
      .filter((fieldObj: any) => Object.keys(fieldObj)[0].startsWith(prefix))
      .map((fieldObj: any) => {
        const name = Object.keys(fieldObj)[0];
        const value = fieldObj[name];
        // Remove prefix and capitalize each word, add space before trailing numbers
        let label = name.replace(new RegExp(`^${prefix}`), "");
        label = label.replace(/([A-Z])/g, " $1").trim();
        label = label.replace(/(\D)(\d+)$/, "$1 $2");
        label = label.replace(/^./, (str) => str.toUpperCase());
        label = label
          .split(" ")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");
        return {
          label,
          name,
          value,
        };
      });
  }

  const mergeFieldArrays = (primaryFields: any[] = [], fallbackFields: any[] = []) => {
    const normalizeKey = (field: any) =>
      String(field?.name ?? field?.key ?? "").toLowerCase();

    const primaryMap = new Map<string, any>();
    primaryFields.forEach((field) => {
      const key = normalizeKey(field);
      if (key) primaryMap.set(key, field);
    });

    const fallbackMap = new Map<string, any>();
    fallbackFields.forEach((field) => {
      const key = normalizeKey(field);
      if (key) fallbackMap.set(key, field);
    });

    const merged = fallbackFields.map((fallbackField) => {
      const key = normalizeKey(fallbackField);
      const primaryField = key ? primaryMap.get(key) : undefined;
      return primaryField
        ? { ...fallbackField, ...primaryField, value: primaryField?.value ?? "" }
        : { ...fallbackField, value: fallbackField?.value ?? "" };
    });

    primaryFields.forEach((primaryField) => {
      const key = normalizeKey(primaryField);
      if (!key || fallbackMap.has(key)) return;
      merged.push({ ...primaryField, value: primaryField?.value ?? "" });
    });

    return merged;
  };

  let addressDetailsToShow = addressDetails;
  if (
    fetchUserApplicationData &&
    fetchUserApplicationData?.partnerStartedJourney &&
    addressDetailsToShow?.length === 0     
  ) {
    addressDetailsToShow = addressDetails || {}
  }

  if (
    pageType === "SUMMARY" &&
    fetchUserApplicationData &&
    !fetchUserApplicationData?.partnerStartedJourney
  ) {
    // Find the 'Applicant & Loan Details' horizontal step
    const applicantLoanStep = fetchUserApplicationData?.horizontalSteps?.find(
      (h: any) => h.horizontalStepName === "Applicant & Loan Details"
    );
    // Find the 'Address Details' vertical step
    const addressDetailsStep = applicantLoanStep?.verticalSteps?.find(
      (v: any) => v.verticalStepName === "Address Details"
    );
    const fields = addressDetailsStep?.fields || [];
    const fallbackAddressDetails = addressDetails || {
      currentAddress: [],
      permanentAddress: [],
    };
    if (addressDetailsStep) {
      const mappedCurrentAddress = mapAddressFields(fields, "current");
      const mappedPermanentAddress = mapAddressFields(fields, "permanent");
      addressDetailsToShow = {
        currentAddress: mergeFieldArrays(
          mappedCurrentAddress,
          fallbackAddressDetails.currentAddress || []
        ),
        permanentAddress: mergeFieldArrays(
          mappedPermanentAddress,
          fallbackAddressDetails.permanentAddress || []
        ),
      };
    } else {
      addressDetailsToShow = {
        currentAddress: mergeFieldArrays(
          [],
          fallbackAddressDetails.currentAddress || []
        ),
        permanentAddress: mergeFieldArrays(
          [],
          fallbackAddressDetails.permanentAddress || []
        ),
      };
    }
  }

  if (!addressDetailsToShow) {
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          No details available.
        </Typography>
      </Grid>
    );
  }

  return (
    <>
      <Grid item xs={12} sx={{ mx: isMobile ? 1 : 0, mt: 2, mb: 1 }}>
        <SummarySectionHeader title="Current Address" />
      </Grid>

      {addressDetailsToShow?.currentAddress?.length > 0 ? (
        addressDetailsToShow?.currentAddress?.map(
          (field: any, subIndex: number) => {
            const skipNames = ["currentaddresstype", "currentsameaspermanent"];
            if (skipNames.includes(field.name.toLowerCase())) return null;
            return (
              <Grid item xs={12} sm={6} md={3} key={`current-${subIndex}`}>
                <DetailRow
                  item={{
                    label: field.label,
                    value: field.value,
                    isVerified: false,
                  }}
                />
              </Grid>
            );
          }
        )
      ) : (
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            No details available.
          </Typography>
        </Grid>
      )}

      <Grid
        item
        xs={12}
        sx={{ mx: isMobile ? 1 : 0, mt: isMobile ? 5 : 4, mb: 1 }}
      >
        <SummarySectionHeader title="Permanent Address" />
      </Grid>
      {addressDetailsToShow?.permanentAddress?.length > 0 ? (
        addressDetailsToShow?.permanentAddress?.map(
          (field: any, subIndex: number) => {
            const skipNames = ["permanentaddresstype"];
            if (skipNames.includes(field.name.toLowerCase())) return null;
            return (
              <Grid item xs={12} sm={6} md={3} key={`permanent-${subIndex}`}>
                <DetailRow
                  item={{
                    label: field.label,
                    value: field.value,
                    isVerified: false,
                  }}
                />
              </Grid>
            );
          }
        )
      ) : (
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            No details available.
          </Typography>
        </Grid>
      )}
    </>
  );
};

export default AddressDetailsTab;
